/**
 * 神劍御雷真訣特效系統
 * S級特殊效果，紫色雷電劍柱具有指向性，落向被翻轉的棋子
 */

export interface Position {
  x: number;
  y: number;
}

export interface LightningPoint {
  x: number;
  y: number;
}

export interface FractalBolt {
  points: LightningPoint[];
  branches: FractalBolt[];
}

export interface ElectricSparkConfig {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

export interface ThunderTarget {
  start: Position;
  end: Position;
  delay: number;
}

/**
 * 神劍御雷主題顏色配置
 */
export const THUNDER_THEME = {
  core: 'rgba(200,160,255,1.0)',      // 核心紫色
  bolt: 'rgba(180,140,255,0.95)',     // 閃電主體
  haze: 'rgba(160,120,255,0.25)',     // 光暈
  spark: 'rgba(220,200,255,0.9)',     // 電火花
  ring: 'rgba(210,190,255,0.65)',     // 衝擊環
  blade: 'rgba(190,160,255,0.9)',     // 劍柱
};

/**
 * 電火花粒子
 */
export class ElectricSpark {
  public x: number;
  public y: number;
  public vx: number;
  public vy: number;
  public time = 0;
  public life: number;
  public dead = false;

  constructor(config: ElectricSparkConfig) {
    this.x = config.x;
    this.y = config.y;
    this.vx = config.vx;
    this.vy = config.vy;
    this.life = config.life;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    if (this.time > this.life) {
      this.dead = true;
      return;
    }

    // 重力影響
    this.vy += 900 * deltaTime;

    // 更新位置
    this.x += this.vx * deltaTime;
    this.y += this.vy * deltaTime;
  }

  draw(ctx: CanvasRenderingContext2D, alpha: number): void {
    if (this.dead) {return;}

    const k = 1 - this.time / this.life;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = alpha * k;
    ctx.strokeStyle = THUNDER_THEME.spark;
    ctx.lineWidth = 2;

    // 繪製十字型電火花
    ctx.beginPath();
    ctx.moveTo(this.x - 2, this.y);
    ctx.lineTo(this.x + 2, this.y);
    ctx.moveTo(this.x, this.y - 2);
    ctx.lineTo(this.x, this.y + 2);
    ctx.stroke();

    ctx.restore();
  }
}

/**
 * 衝擊波環
 */
export class ShockwaveRing {
  public x: number;
  public y: number;
  public time = 0;
  public duration = 1.0;
  public dead = false;
  public maxRadius = 180;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  update(deltaTime: number): void {
    this.time += deltaTime * 2.2;

    if (this.time >= this.duration * 1.1) {
      this.dead = true;
    }
  }

  draw(ctx: CanvasRenderingContext2D, alpha: number): void {
    if (this.dead || this.time <= 0) {return;}

    const progress = Math.min(1, this.time / this.duration);
    const radius = 20 + progress * this.maxRadius;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = alpha * (1 - progress);
    ctx.strokeStyle = THUNDER_THEME.ring;
    ctx.lineWidth = 3 + 6 * (1 - progress);

    ctx.beginPath();
    ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
  }
}

/**
 * 分形閃電生成器
 */
export class FractalLightningGenerator {
  /**
   * 生成分形閃電路徑
   */
  static generateBolt(
    start: Position,
    end: Position,
    depth = 5,
    displacement = 48,
    branchProbability = 0.35,
  ): FractalBolt {
    const points: LightningPoint[] = [
      { x: start.x, y: start.y },
      { x: end.x, y: end.y },
    ];

    // 中點位移算法
    for (let d = 0; d < depth; d++) {
      for (let i = points.length - 1; i > 0; i--) {
        const a = points[i - 1];
        const b = points[i];

        if (!a || !b) {continue;}

        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;

        // 計算垂直方向
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const length = Math.hypot(dx, dy) || 1;

        // 垂直向量（旋轉90度）
        const perpX = -dy / length;
        const perpY = dx / length;

        // 隨機位移
        const offset = (Math.random() * 2 - 1) * displacement;

        points.splice(i, 0, {
          x: midX + perpX * offset,
          y: midY + perpY * offset,
        });
      }
      displacement *= 0.55; // 遞減位移量
    }

    // 生成分支
    const branches: FractalBolt[] = [];
    for (let i = 2; i < points.length - 2; i++) {
      if (Math.random() < branchProbability) {
        const point = points[i];
        const nextPoint = points[i + 1];

        if (!point || !nextPoint) {continue;}

        const dirX = nextPoint.x - point.x;
        const dirY = nextPoint.y - point.y;
        const length = Math.hypot(dirX, dirY) || 1;
        const scale = 0.35 + Math.random() * 0.25;

        const branchEnd = {
          x: point.x + (dirX / length) * (40 + Math.random() * 50),
          y: point.y + (dirY / length) * (40 + Math.random() * 50),
        };

        branches.push(this.generateBolt(
          point,
          branchEnd,
          3,
          displacement * 0.6,
          0.15,
        ));
      }
    }

    return { points, branches };
  }

  /**
   * 繪製分形閃電
   */
  static drawBolt(ctx: CanvasRenderingContext2D, bolt: FractalBolt, baseWidth = 3): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    // 外暈（淡紫）
    ctx.strokeStyle = THUNDER_THEME.haze;
    ctx.lineWidth = baseWidth * 4;
    this.pathBolt(ctx, bolt);
    ctx.stroke();

    // 主體（亮紫）
    ctx.strokeStyle = THUNDER_THEME.bolt;
    ctx.lineWidth = baseWidth;
    this.pathBolt(ctx, bolt);
    ctx.stroke();

    // 中芯高光（白亮線）
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = Math.max(1, baseWidth * 0.5);
    this.pathBolt(ctx, bolt);
    ctx.stroke();

    // 繪製分支
    for (const branch of bolt.branches) {
      this.drawBolt(ctx, branch, baseWidth * 0.7);
    }

    ctx.restore();
  }

  /**
   * 創建閃電路徑
   */
  private static pathBolt(ctx: CanvasRenderingContext2D, bolt: FractalBolt): void {
    const points = bolt.points;
    if (points.length < 2) {return;}

    ctx.beginPath();
    const firstPoint = points[0];
    if (firstPoint) {
      ctx.moveTo(firstPoint.x, firstPoint.y);
    }

    for (let i = 1; i < points.length; i++) {
      const point = points[i];
      if (point) {
        ctx.lineTo(point.x, point.y);
      }
    }
  }
}

/**
 * 雷電劍柱
 */
export class ThunderBlade {
  public x: number;
  public y: number;
  public targetY: number;
  public currentY: number;
  public width = 14;
  public arrived = false;
  public alpha = 1;

  constructor(x: number, targetY: number) {
    this.x = x;
    this.targetY = targetY;
    this.y = targetY;
    this.currentY = -160; // 從上方開始
  }

  update(deltaTime: number): void {
    // 快速下降到目標位置
    const speed = 16;
    this.currentY += (this.targetY - this.currentY) * Math.min(1, deltaTime * speed);

    if (Math.abs(this.currentY - this.targetY) < 6) {
      this.arrived = true;
    }
  }

  draw(ctx: CanvasRenderingContext2D, alpha: number): void {
    const effectiveAlpha = alpha * this.alpha;
    const topY = Math.min(this.currentY, this.targetY - window.innerHeight * 0.35);
    const bottomY = this.targetY + 10;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // 外暈
    ctx.globalAlpha = effectiveAlpha * 0.6;
    const gradient1 = ctx.createLinearGradient(this.x, topY, this.x, bottomY);
    gradient1.addColorStop(0, 'rgba(170,120,255,0)');
    gradient1.addColorStop(0.1, 'rgba(170,120,255,0.25)');
    gradient1.addColorStop(0.9, 'rgba(170,120,255,0.25)');
    gradient1.addColorStop(1, 'rgba(170,120,255,0)');
    ctx.fillStyle = gradient1;
    ctx.fillRect(this.x - 28, topY, 56, bottomY - topY);

    // 劍身
    ctx.globalAlpha = effectiveAlpha;
    ctx.fillStyle = THUNDER_THEME.blade;
    ctx.fillRect(this.x - this.width / 2, topY, this.width, bottomY - topY);

    // 中芯白光
    ctx.globalAlpha = effectiveAlpha;
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.fillRect(this.x - 1, topY + 6, 2, (bottomY - topY) - 12);

    // 劍尖圓光
    if (this.arrived) {
      ctx.globalAlpha = effectiveAlpha;
      const radialGradient = ctx.createRadialGradient(
        this.x, bottomY - 6, 0,
        this.x, bottomY - 6, 22,
      );
      radialGradient.addColorStop(0, 'rgba(255,255,255,1)');
      radialGradient.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = radialGradient;
      ctx.beginPath();
      ctx.arc(this.x, bottomY - 6, 22, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}

/**
 * 神劍御雷主效果
 */
export class DivineSwordThunderEffect {
  public x: number;
  public y: number;
  public dead = false;
  private time = 0;
  private duration = 2.0; // S級持續時間
  private targetPositions: Position[];
  private thunderBlades: ThunderBlade[] = [];
  private lightningBolts: FractalBolt[] = [];
  private sparks: ElectricSpark[] = [];
  private shockwaves: ShockwaveRing[] = [];
  private screenShake = 0;
  private thunderStarted = false;

  constructor(x: number, y: number, targetPositions: Position[] = []) {
    this.x = x;
    this.y = y;
    this.targetPositions = targetPositions;
    this.initializeBlades();
  }

  private initializeBlades(): void {
    if (this.targetPositions.length > 0) {
      // 為每個翻轉位置創建劍柱
      this.targetPositions.forEach(target => {
        this.thunderBlades.push(new ThunderBlade(target.x, target.y));
      });
    } else {
      // 沒有目標時在中心創建一把劍
      this.thunderBlades.push(new ThunderBlade(this.x, this.y));
    }
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    if (this.time > this.duration) {
      this.dead = true;
      return;
    }

    // 更新劍柱
    let anyBladeArrived = false;
    this.thunderBlades.forEach(blade => {
      blade.update(deltaTime);
      if (blade.arrived) {
        anyBladeArrived = true;
      }
    });

    // 劍柱到達後開始雷電效果
    if (anyBladeArrived && !this.thunderStarted) {
      this.startThunderEffect();
      this.thunderStarted = true;
    }

    // 持續生成閃電（如果已開始）
    if (this.thunderStarted) {
      this.generateLightning();
    }

    // 更新震動衰減
    if (this.screenShake > 0) {
      this.screenShake = Math.max(0, this.screenShake - deltaTime * 24);
    }

    // 更新火花
    this.sparks.forEach(spark => spark.update(deltaTime));
    this.sparks = this.sparks.filter(spark => !spark.dead);

    // 更新衝擊波
    this.shockwaves.forEach(wave => wave.update(deltaTime));
    this.shockwaves = this.shockwaves.filter(wave => !wave.dead);

    // 控制閃電數量避免過多
    if (this.lightningBolts.length > 12) {
      this.lightningBolts.splice(0, this.lightningBolts.length - 12);
    }
  }

  private startThunderEffect(): void {
    // 強烈震動
    this.screenShake = 6;

    // 在每個劍柱位置生成火花和衝擊波
    this.thunderBlades.forEach(blade => {
      this.spawnSparks(blade.x, blade.y, 20);
      this.shockwaves.push(new ShockwaveRing(blade.x, blade.y));
    });
  }

  private generateLightning(): void {
    // 每幀生成2-4道閃電
    const count = 2 + (Math.random() < 0.5 ? 1 : 2);

    for (let i = 0; i < count; i++) {
      // 隨機選擇一個劍柱作為目標
      const targetBlade = this.thunderBlades[Math.floor(Math.random() * this.thunderBlades.length)];
      if (!targetBlade) {continue;}

      // 閃電起點在上方隨機位置
      const startX = targetBlade.x + (Math.random() - 0.5) * window.innerWidth * 0.5;
      const startY = -80 + Math.random() * window.innerHeight * 0.1;

      const bolt = FractalLightningGenerator.generateBolt(
        { x: startX, y: startY },
        { x: targetBlade.x, y: targetBlade.y },
        5,
        32 + Math.random() * 32,
        0.35,
      );

      this.lightningBolts.push(bolt);
    }
  }

  private spawnSparks(x: number, y: number, count: number): void {
    for (let i = 0; i < count; i++) {
      this.sparks.push(new ElectricSpark({
        x,
        y,
        vx: -220 + Math.random() * 440,
        vy: -180 + Math.random() * 160,
        life: 0.15 + Math.random() * 0.2,
      }));
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.dead) {return;}

    // 計算淡入淡出
    let alpha = 1;
    const fadeInTime = 0.12;
    const fadeOutTime = 0.25;

    if (this.time < fadeInTime) {
      alpha = this.time / fadeInTime;
    } else if (this.time > this.duration - fadeOutTime) {
      alpha = (this.duration - this.time) / fadeOutTime;
    }

    // 應用震動
    ctx.save();
    if (this.screenShake > 0) {
      const shakeX = (Math.random() - 0.5) * this.screenShake;
      const shakeY = (Math.random() - 0.5) * this.screenShake;
      ctx.translate(shakeX, shakeY);
    }

    // 繪製劍柱
    this.thunderBlades.forEach(blade => blade.draw(ctx, alpha));

    // 繪製閃電
    this.lightningBolts.forEach(bolt => {
      FractalLightningGenerator.drawBolt(ctx, bolt, 2.6);
    });

    // 繪製衝擊波
    this.shockwaves.forEach(wave => wave.draw(ctx, alpha));

    // 繪製火花
    this.sparks.forEach(spark => spark.draw(ctx, alpha));

    ctx.restore();
  }
}